import {
  PricingSummary,
  PricingSummaryRange,
  Phase,
  PricingLevel,
  PricingType,
  PriceRange,
  GpmType,
  FixedGpm,
  RangeGpm,
  isPrice,
  Price,
  isPriceRange,
  isPricingSummary,
  isPricingSummaryRange,
  pricingFieldsByType,
  joinPricing,
  isSinglePrice,
  PricingOptions,
  Work,
  Uplift,
  UpliftType,
} from "../../data-types";
export const sumLegalFeePriceSingle = (
  pricingOptions: PricingSummary,
  phases: Phase[] | null | undefined,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  matterWork: Work[] | null,
  topDownRateDiscount: number
): number => {
  if (pricingType === PricingType.None) return 0;

  let legalFees = 0;

  if (
    pricingLevel === PricingLevel.Matter &&
    pricingType !== PricingType.EstimatedRange &&
    pricingType !== PricingType.TopDown
  ) {
    legalFees = (pricingOptions?.chosen as number) || 0;
  } else if (pricingLevel === PricingLevel.Phase) {
    legalFees =
      phases?.reduce(
        (acc, p) =>
          acc + ((pricingFieldsByType(pricingType, p.priceOptions!)?.chosen as number) || 0),
        0
      ) ?? 0;
  } else if (pricingLevel === PricingLevel.FeeEarner) {
    legalFees =
      phases
        ?.map((p) => {
          const phasePrice =
            (pricingFieldsByType(pricingType, p.priceOptions!)?.chosen as number) ??
            sumWorkPriceSingle(p.work);
          return phasePrice;
        })
        .reduce((acc, val) => acc + val, 0) ?? 0;
  } else if (pricingLevel === PricingLevel.Matter && pricingType === PricingType.TopDown) {
    legalFees = pricingOptions?.chosen
      ? (pricingOptions?.chosen as number)
      : (1 - (topDownRateDiscount ?? 0) / 100) * sumWorkPriceSingle(matterWork ?? null);
    legalFees = Number(legalFees.toFixed(2));
  }
  return legalFees;
};

export const sumLegalFeePriceRange = (
  pricingSummary: PricingSummaryRange,
  phases: Phase[] | null | undefined,
  pricingLevel: PricingLevel
): { lower: number; upper: number } => {
  let legalFees = { lower: 0, upper: 0 };

  if (pricingSummary == null || pricingSummary?.chosen == null) return legalFees;

  if (pricingLevel === PricingLevel.Matter) {
    legalFees = {
      lower: pricingSummary.chosen?.lower || 0,
      upper: pricingSummary.chosen?.upper || 0,
    };
  } else if (pricingLevel === PricingLevel.Phase) {
    legalFees = phases?.reduce(
      (acc, p) => {
        const tranformed: PriceRange = pricingFieldsByType(
          PricingType.EstimatedRange,
          p.priceOptions!
        ).chosen;

        const lower = tranformed?.lower || 0;
        const upper = tranformed?.upper || 0;
        return {
          lower: acc.lower + lower,
          upper: acc.upper + upper,
        };
      },
      { lower: 0, upper: 0 }
    ) ?? { lower: 0, upper: 0 };
  } else if (pricingLevel === PricingLevel.FeeEarner) {
    legalFees = phases
      ?.map((p) => {
        const chosenPrice = pricingFieldsByType(PricingType.EstimatedRange, p.priceOptions!)
          .chosen as PriceRange;
        const workSum = sumWorkPriceRange(p.work);
        return {
          lower: chosenPrice?.lower ?? workSum.lower,
          upper: chosenPrice?.upper ?? workSum.upper,
        };
      })
      .reduce(
        (acc, val) => ({
          lower: acc.lower + (val.lower || 0),
          upper: acc.upper + (val.upper || 0),
        }),
        { lower: 0, upper: 0 }
      ) ?? { lower: 0, upper: 0 };
  }
  return legalFees;
};
export const sumWorkPriceRange = (work: Work[] | null): { lower: number; upper: number } =>
  work?.reduce(
    (acc, { price: { lower, upper } }) => ({
      lower: acc.lower + (lower || 0),
      upper: acc.upper + (upper || 0),
    }),
    { lower: 0, upper: 0 }
  ) || { lower: 0, upper: 0 };

export const sumWorkPriceSingle = (work: Work[] | null): number =>
  work?.reduce((acc, { price: { single } }) => acc + (single || 0), 0) || 0;

const sumWorkCostSingle = (work: Work[] | null): number =>
  work?.reduce((acc, { cost: { single } }) => acc + (single || 0), 0) || 0;

const sumWorkCostRange = (work: Work[] | null): { lower: number; upper: number } =>
  work?.reduce(
    (acc, { cost: { lower, upper } }) => ({
      lower: acc.lower + (lower || 0),
      upper: acc.upper + (upper || 0),
    }),
    { lower: 0, upper: 0 }
  ) || { lower: 0, upper: 0 };

export const sumMatterCostRange = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType
): { lower: number; upper: number } | null => {
  if (pricingLevel !== PricingLevel.FeeEarner && pricingType !== PricingType.TopDown) {
    return null;
  }

  const matterCost = sumWorkCostRange(matterWork);
  const phaseCost = phases?.reduce(
    (acc, p) => {
      const phaseTotal = sumWorkCostRange(p.work);
      return {
        lower: acc.lower + phaseTotal.lower,
        upper: acc.upper + phaseTotal.upper,
      };
    },
    { lower: 0, upper: 0 }
  ) || { lower: 0, upper: 0 };

  return {
    lower: matterCost.lower + phaseCost.lower,
    upper: matterCost.upper + phaseCost.upper,
  };
};

export const sumMatterCostSingle = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType
): number | null => {
  if (pricingLevel !== PricingLevel.FeeEarner && pricingType !== PricingType.TopDown) {
    return null;
  }

  const matterCost = sumWorkCostSingle(matterWork);
  const phaseCost = phases?.reduce((acc, p) => acc + sumWorkCostSingle(p.work), 0) ?? 0;
  const totalCost = matterCost + phaseCost;
  return totalCost > 0 ? totalCost : null;
};

export const sumMatterWorkPriceSingle = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  topDownRateDiscount: number | null | undefined
): number => {
  const matterWorkPrice = (1 - (topDownRateDiscount ?? 0) / 100) * sumWorkPriceSingle(matterWork);
  const phaseWorkPrice =
    phases?.map((phase) => sumWorkPriceSingle(phase.work)).reduce((acc, val) => acc + val, 0) ?? 0;
  return matterWorkPrice + phaseWorkPrice;
};

export const sumMatterWorkPriceRange = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  topDownRateDiscount: number | null | undefined
): { lower: number; upper: number } => {
  const matterWorkPriceLower =
    (1 - (topDownRateDiscount ?? 0) / 100) * sumWorkPriceRange(matterWork).lower;
  const matterWorkPriceUpper =
    (1 - (topDownRateDiscount ?? 0) / 100) * sumWorkPriceRange(matterWork).upper;
  const phaseWorkPriceLower =
    phases
      ?.map((phase) => sumWorkPriceRange(phase.work).lower)
      .reduce((acc, val) => acc + val, 0) ?? 0;
  const phaseWorkPriceUpper =
    phases
      ?.map((phase) => sumWorkPriceRange(phase.work).upper)
      .reduce((acc, val) => acc + val, 0) ?? 0;
  return {
    lower: matterWorkPriceLower + phaseWorkPriceLower,
    upper: matterWorkPriceUpper + phaseWorkPriceUpper,
  };
};

export const priceOrPriceRangeOrPricingSummaryOrPricingSummaryRangeIsZero = (
  priceOrPriceRange: Price | PriceRange | PricingSummary | PricingSummaryRange | undefined
): boolean => {
  if (priceOrPriceRange == null) {
    return true;
  }
  if (isPrice(priceOrPriceRange)) {
    return priceOrPriceRange.amount === 0;
  }
  if (isPriceRange(priceOrPriceRange)) {
    return priceOrPriceRange.lower === 0 && priceOrPriceRange.upper === 0;
  }
  if (isPricingSummary(priceOrPriceRange)) {
    return (
      priceOrPriceRange.chosen === 0 ||
      priceOrPriceRange.chosen === null ||
      priceOrPriceRange.chosen === undefined
    );
  }
  if (isPricingSummaryRange(priceOrPriceRange)) {
    return (
      priceOrPriceRange.chosen?.lower === 0 &&
      priceOrPriceRange.chosen?.upper === 0 &&
      priceOrPriceRange.chosen?.lower === null &&
      priceOrPriceRange.chosen?.upper === null
    );
  }
  return true;
};

export const pricingTypeIsZero = (
  pricingType: PricingType,
  pricingOptions: PricingOptions
): boolean => {
  if (pricingType === PricingType.None) return true;
  if (pricingType === PricingType.Fixed) return pricingOptions.fixedChosen === 0;
  if (pricingType === PricingType.Capped) return pricingOptions.cappedChosen === 0;
  if (pricingType === PricingType.EstimatedRange) {
    return (
      pricingOptions.estimatedRangeChosenLower === 0 &&
      pricingOptions.estimatedRangeChosenUpper === 0
    );
  }
  return false;
};

export const calculateGpm = (
  cost: number | null | undefined,
  price: number | null | undefined
): FixedGpm | null => {
  if (cost == null || price == null || cost === 0 || price === 0) {
    return null;
  }
  return { type: GpmType.Fixed, fixed: ((price - cost) / price) * 100 };
};

type MixedGpmInput = {
  cost: (number | Price | PriceRange | undefined | null)[] | undefined;
  price: (number | Price | PriceRange | undefined | null)[] | undefined;
};

export const calculateMixedGpm = (args: MixedGpmInput) => {
  const { cost, price } = args;
  if (!cost || !price) {
    return null;
  }
  const totalCost = joinPricing(cost);
  const totalPrice = joinPricing(price);
  if (isSinglePrice(totalCost) && isSinglePrice(totalPrice)) {
    return calculateGpm(totalCost, totalPrice);
  }
  if (isPriceRange(totalCost) && isPriceRange(totalPrice)) {
    return calculateGpmRange(totalCost, totalPrice);
  }
  if (isSinglePrice(totalCost) && isPriceRange(totalPrice)) {
    return calculateGpm(totalCost, totalPrice.lower);
  }
  if (isPriceRange(totalCost) && isSinglePrice(totalPrice)) {
    return calculateGpmRange(totalCost, { lower: totalPrice, upper: totalPrice });
  }
  return null;
};

export const calculateGpmRange = (
  cost: PriceRange | null,
  price: PriceRange | null
): RangeGpm | null => {
  if (
    cost == null ||
    price == null ||
    cost.lower == null ||
    cost.upper == null ||
    price.lower == null ||
    price.upper == null ||
    cost.lower === 0 ||
    cost.upper === 0 ||
    price.lower === 0 ||
    price.upper === 0
  ) {
    return null;
  }
  const lowerGpm = ((price.lower - cost.lower) / price.lower) * 100;
  const upperGpm = ((price.upper - cost.upper) / price.upper) * 100;
  return { type: GpmType.Range, lower: lowerGpm, upper: upperGpm };
};

/**
 * Split uplifts into partial (hidden) and visible (itemized) groups
 */
export const splitUplifts = (uplifts: Uplift[] | null) => ({
  partial: uplifts?.filter((u) => !u.includeOnProposal) ?? [],
  visible: uplifts?.filter((u) => u.includeOnProposal) ?? [],
});

/**
 * Sum uplifts against a given fee amount
 */
export const sumUplifts = (uplifts: Uplift[], fee: number) =>
  uplifts.reduce(
    (acc, { amount, type }) =>
      type === UpliftType.Amount ? acc + amount : acc + (amount * fee) / 100,
    0
  );
